import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { countryConfig } from '../country';
import { useLocale } from '../i18n/locale-context';
import { LocaleLink, useLocaleNavigate } from '../i18n/locale-link';
import { useAuth } from '../lib/auth-context';
import {
  fetchAwardedSuggest,
  fetchCategories,
  fetchRegions,
  searchIkpu,
  type ProcurementKind,
} from '../lib/catalog-client';
import { formatCount, pluralRu } from '../lib/format';
import { track } from '../lib/funnel';
import { stashPendingSavedSearch } from '../lib/pending-saved-search';
import {
  diffResultsState,
  fromResultsCriteria,
  hasResultsState,
  isNoChange,
  normalizeWord,
  RESULTS_MAX_CHIPS,
  toResultsCriteria,
  type ResultsCardState,
  type ResultsChange,
  type ResultsCriteria,
} from '../lib/results-search';
import { getResultsSearch, saveResultsSearch } from '../lib/saved-search-client';
import { CheckIcon, SearchIcon, SlidersIcon } from './icons';
import { LockedChip } from './locked-chip';

/** «результат / результата / результатов»; uz «natija», kk «нәтиже» (neither
 *  agrees with a numeral). */
export function resultsWord(n: number, locale: string): string {
  if (locale === 'uz') return 'natija';
  if (locale === 'kk') return 'нәтиже';
  if (locale === 'es') return n === 1 ? 'resultado' : 'resultados';
  return pluralRu(n, 'результат', 'результата', 'результатов');
}

const KINDS: ProcurementKind[] = ['goods', 'works', 'services'];

/** Four-square glyph — «a shelf of our catalogue», the category/ИКПУ chip mark. */
function ShelfGlyph({ className = 'h-3 w-3' }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className={className}>
      <rect x="2" y="2" width="5" height="5" rx="1.2" />
      <rect x="9" y="2" width="5" height="5" rx="1.2" />
      <rect x="2" y="9" width="5" height="5" rx="1.2" />
      <rect x="9" y="9" width="5" height="5" rx="1.2" />
    </svg>
  );
}

function XGlyph() {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
      className="h-2.5 w-2.5"
    >
      <path strokeLinecap="round" d="M3 3l6 6M9 3l-6 6" />
    </svg>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  const { t } = useLocale();
  return (
    <button
      type="button"
      aria-label={t('results.card.remove', { label })}
      onClick={onClick}
      className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full opacity-55 transition-opacity hover:opacity-100"
    >
      <XGlyph />
    </button>
  );
}

/** WORD chip: a white pill — free text, matched anywhere. */
function WordChip({ word, onRemove }: { word: string; onRemove: () => void }) {
  return (
    <span className="inline-flex h-[30px] max-w-full items-center gap-1 rounded-pill border border-[#d6d4cd] bg-bone-surface pl-[11px] pr-1 text-[13.5px] font-semibold text-ink">
      <span className="truncate">{word}</span>
      <RemoveButton label={word} onClick={onRemove} />
    </span>
  );
}

/** CATEGORY / ИКПУ chip: a squared teal tag — a shelf of our catalogue. */
function ShelfChip({
  prefix,
  value,
  onRemove,
}: {
  prefix: string;
  value: string;
  onRemove: () => void;
}) {
  return (
    <span
      data-testid="results-shelf-chip"
      className="inline-flex h-[30px] max-w-full items-center gap-[5px] rounded-sm border border-brand/20 bg-brand-light pl-2 pr-1 text-[13.5px] text-brand"
    >
      <ShelfGlyph className="h-3 w-3 shrink-0 opacity-75" />
      <span className="shrink-0 font-medium opacity-80">{prefix}</span>
      <span className="truncate font-bold">{value}</span>
      <RemoveButton label={value} onClick={onRemove} />
    </span>
  );
}

/** A code's label: what the suggestion carried, else the reference lookup. */
function useIkpuName(code: string, known: string | undefined, locale: string): string | undefined {
  const { data } = useQuery({
    queryKey: ['ikpu-label', code, locale],
    queryFn: async () => {
      const rows = await searchIkpu(code, locale, 5);
      return rows.find((r) => r.code === code)?.name ?? null;
    },
    enabled: !known,
    staleTime: 60 * 60_000,
  });
  return known ?? data ?? undefined;
}

function IkpuChip({
  code,
  known,
  onRemove,
}: {
  code: string;
  known: string | undefined;
  onRemove: () => void;
}) {
  const { t, locale } = useLocale();
  const name = useIkpuName(code, known, locale);
  return (
    <ShelfChip
      prefix={t('results.card.ikpu')}
      value={name ? `${code} ${name}` : code}
      onRemove={onRemove}
    />
  );
}

type SuggestRow =
  | { type: 'word'; value: string }
  | { type: 'category'; slug: string; name: string; count: number }
  | { type: 'ikpu'; code: string; name: string; count: number };

/** Mark where the typed text starts a word in a suggestion's name. */
function Highlight({ text, term }: { text: string; term: string }) {
  const i = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <b className="text-brand">{text.slice(i, i + term.length)}</b>
      {text.slice(i + term.length)}
    </>
  );
}

export interface ResultsSearchCardProps {
  state: ResultsCardState;
  onChange: (next: ResultsCardState) => void;
  /** «за месяц · за неделю» for the current card; undefined while loading. */
  summary: { month: number; week: number; today: number } | undefined;
  /** The page-level wall line (non-paid viewer, UZ only). */
  showWall: boolean;
  /**
   * The highlighted first ask (owner, 2026-10-07): for a guest, or an account
   * with NO saved search of any kind (lots or results). The card takes the
   * catalogue's spotlight look (b4d14844 — double orange border, warm fill,
   * shadow). Its heading stays «Что вы поставляете?» (Igor, 10.10: the long
   * spotlight question made the card look crammed).
   * Decided by the page from payloads it already has; static text, so it
   * prerenders as-is for the guest.
   */
  spotlight?: boolean;
  /** Rides the right end of the count line — /results puts the Pro Excel
   *  export there, where the catalogue keeps its own (beside the count). */
  countTrailing?: ReactNode;
}

/**
 * «Что вы поставляете?» — the ONE control on /results (owner, 2026-10-07).
 *
 * Replaces the topbar search, the kind tabs and the filter bar on this page:
 * a chip input (word chips and category/ИКПУ chips look different on purpose —
 * a word is matched anywhere, a category is a shelf of the catalogue),
 * suggestions with their awarded-archive counts, a collapsed «Уточнить» panel,
 * the count line and «Сохранить поиск». Everything it edits lives in the URL
 * (`lib/results-search.ts`), so links, back/forward and the prerender keep
 * working; the list below filters live from it.
 *
 * Saving makes the reader's ONE `results` search (`PUT /me/results-search`);
 * a second save updates it. Alerts are not configured here — the existing
 * onboarding / alerts settings do that, unchanged.
 */
export function ResultsSearchCard({
  state,
  onChange,
  summary,
  showWall,
  spotlight = false,
  countTrailing,
}: ResultsSearchCardProps) {
  const { t, locale } = useLocale();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [debounced, setDebounced] = useState('');
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  // Names of ИКПУ codes picked from a suggestion — no lookup needed for those.
  const [ikpuNames, setIkpuNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(draft.trim()), 250);
    return () => window.clearTimeout(id);
  }, [draft]);

  const { data: categories } = useQuery({
    queryKey: ['catalog', 'categories', locale],
    queryFn: () => fetchCategories(locale),
    staleTime: 10 * 60_000,
  });
  const categoryName = (slug: string) =>
    categories?.find((c) => c.slug === slug)?.name ??
    categories?.find((c) => c.slug.split(',').includes(slug))?.name ??
    slug;

  const { data: suggestions } = useQuery({
    queryKey: ['catalog', 'awarded-suggest', debounced, locale],
    queryFn: () => fetchAwardedSuggest(debounced, locale),
    enabled: debounced.length >= 2,
    staleTime: 5 * 60_000,
  });

  const word = normalizeWord(draft);
  const rows: SuggestRow[] = useMemo(() => {
    const out: SuggestRow[] = [];
    if (word && !state.words.includes(word)) out.push({ type: 'word', value: word });
    if (debounced && debounced === draft.trim()) {
      for (const c of suggestions?.categories ?? []) {
        if (!state.categories.includes(c.slug)) out.push({ type: 'category', ...c });
      }
      for (const c of suggestions?.ikpu ?? []) {
        if (!state.ikpu.includes(c.code)) out.push({ type: 'ikpu', ...c });
      }
    }
    return out;
  }, [word, debounced, draft, suggestions, state.words, state.categories, state.ikpu]);
  const open = focused && rows.length > 0;
  useEffect(() => setActive(0), [draft]);

  function commit(row: SuggestRow | undefined) {
    if (!row) return;
    if (row.type === 'word') {
      if (state.words.length >= RESULTS_MAX_CHIPS) return;
      onChange({ ...state, words: [...state.words, row.value] });
    } else if (row.type === 'category') {
      if (state.categories.length >= RESULTS_MAX_CHIPS) return;
      onChange({ ...state, categories: [...state.categories, row.slug] });
    } else {
      if (state.ikpu.length >= RESULTS_MAX_CHIPS) return;
      setIkpuNames((m) => ({ ...m, [row.code]: row.name }));
      onChange({ ...state, ikpu: [...state.ikpu, row.code] });
    }
    setDraft('');
    setDebounced('');
  }

  function removeLast() {
    if (state.ikpu.length) onChange({ ...state, ikpu: state.ikpu.slice(0, -1) });
    else if (state.categories.length)
      onChange({ ...state, categories: state.categories.slice(0, -1) });
    else if (state.words.length) onChange({ ...state, words: state.words.slice(0, -1) });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      setActive((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(open ? rows[active] : word ? { type: 'word', value: word } : undefined);
    } else if (e.key === ',') {
      e.preventDefault();
      if (word) commit({ type: 'word', value: word });
    } else if (e.key === 'Escape') {
      setFocused(false);
    } else if (e.key === 'Backspace' && draft === '') {
      removeLast();
    }
  }

  const chipCount = state.words.length + state.categories.length + state.ikpu.length;

  return (
    <section
      aria-labelledby={`${listId}-title`}
      className={
        spotlight
          ? // The catalogue's spotlight card (filter-bar `spotlight`), class for class.
            'min-w-0 rounded-xl border-2 border-orange/50 bg-orange/10 p-5 shadow-m-sm md:max-w-5xl md:px-6'
          : 'min-w-0 rounded-xl border border-edge bg-bone-surface p-5 shadow-m-sm md:max-w-5xl md:px-6'
      }
      data-testid="results-search-card"
      data-spotlight={spotlight ? 'true' : undefined}
    >
      {/* No emoji: old Android fonts and headless renderers draw a box. */}
      <h2
        id={`${listId}-title`}
        className="font-brand text-[20px] font-extrabold leading-[1.2] tracking-[-0.3px] text-ink"
      >
        {t('results.card.title')}
      </h2>
      <p className="mb-4 mt-1.5 text-[14px] leading-[1.45] text-ink-soft">
        {t('results.card.sub')}
      </p>

      <div className="relative">
        <div
          onClick={() => inputRef.current?.focus()}
          className={`relative flex min-h-[48px] flex-wrap items-center gap-1.5 rounded-md border-1.5 py-[7px] pl-[38px] pr-2.5 transition-colors ${
            focused
              ? 'border-brand bg-bone-surface shadow-[0_0_0_3px_rgba(10,92,92,0.1)]'
              : 'border-edge bg-bone'
          }`}
        >
          <SearchIcon className="pointer-events-none absolute left-[13px] top-[15px] h-4 w-4 text-ink-muted" />
          {state.words.map((w) => (
            <WordChip
              key={`w:${w}`}
              word={w}
              onRemove={() => onChange({ ...state, words: state.words.filter((x) => x !== w) })}
            />
          ))}
          {state.categories.map((slug) => (
            <ShelfChip
              key={`c:${slug}`}
              prefix={t('results.card.category')}
              value={categoryName(slug)}
              onRemove={() =>
                onChange({ ...state, categories: state.categories.filter((x) => x !== slug) })
              }
            />
          ))}
          {state.ikpu.map((code) => (
            <IkpuChip
              key={`i:${code}`}
              code={code}
              known={ikpuNames[code]}
              onRemove={() => onChange({ ...state, ikpu: state.ikpu.filter((x) => x !== code) })}
            />
          ))}
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${listId}-list`}
            aria-autocomplete="list"
            aria-label={t('results.card.inputLabel')}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 64))}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={chipCount === 0 ? t('results.card.placeholder') : ''}
            className="min-w-[8rem] flex-1 bg-transparent px-0.5 py-0.5 text-base font-medium text-ink outline-none placeholder:font-normal placeholder:text-ink-muted md:text-[14px]"
          />
        </div>

        {open && (
          <ul
            id={`${listId}-list`}
            role="listbox"
            className="absolute left-0 right-0 z-20 mt-1.5 overflow-hidden rounded-md border border-edge bg-bone-surface shadow-[0_4px_12px_rgba(26,26,46,0.08),0_2px_4px_rgba(26,26,46,0.04)]"
          >
            {rows.map((row, i) => (
              <li
                key={
                  row.type === 'word'
                    ? 'word'
                    : row.type === 'category'
                      ? `c:${row.slug}`
                      : `i:${row.code}`
                }
                role="option"
                aria-selected={i === active}
                // mousedown, not click: the input's blur would close the list first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(row);
                }}
                onMouseEnter={() => setActive(i)}
                className={`flex cursor-pointer items-center gap-2.5 border-b border-edge-light px-3 py-2.5 text-[14px] ${
                  i === active ? 'bg-brand/[0.07]' : ''
                }`}
              >
                {row.type === 'word' ? (
                  <>
                    <span className="inline-flex h-[22px] w-[26px] shrink-0 items-center justify-center rounded-pill border border-[#d6d4cd] bg-bone-surface text-[11px] font-bold text-ink-soft">
                      Aa
                    </span>
                    <span className="min-w-0 flex-1 text-ink">
                      {t('results.suggest.word')} <b className="text-brand">{row.value}</b>
                    </span>
                    <span className="hidden whitespace-nowrap text-[12px] text-ink-muted sm:inline">
                      {t('results.suggest.wordHint')}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="inline-flex h-[22px] w-[26px] shrink-0 items-center justify-center rounded-[5px] bg-brand-light text-brand">
                      <ShelfGlyph className="h-[11px] w-[11px]" />
                    </span>
                    <span className="min-w-0 flex-1 leading-[1.35] text-ink">
                      {row.type === 'category' ? (
                        <>
                          {t('results.card.category')}{' '}
                          <Highlight text={row.name} term={draft.trim()} />
                        </>
                      ) : (
                        <>
                          {t('results.card.ikpu')} {row.code}{' '}
                          <Highlight text={row.name} term={draft.trim()} />
                        </>
                      )}
                    </span>
                    <span className="whitespace-nowrap rounded-pill bg-bone px-2 py-0.5 text-[12px] font-bold tabular-nums text-ink-soft">
                      {formatCount(row.count)} {resultsWord(row.count, locale)}
                    </span>
                  </>
                )}
              </li>
            ))}
            <li
              aria-hidden="true"
              className="bg-bone-hover px-3 py-[7px] text-[11.5px] text-ink-muted"
            >
              {t('results.suggest.foot')}
            </li>
          </ul>
        )}
      </div>

      <RefinePanel state={state} onChange={onChange} />

      <CardFooter
        state={state}
        summary={summary}
        showWall={showWall}
        countTrailing={countTrailing}
        categoryName={categoryName}
        ikpuNames={ikpuNames}
      />
    </section>
  );
}

/** «Уточнить ▾» — region, sum, exclusions, and (behind «Ещё») kind and buyer. */
function RefinePanel({
  state,
  onChange,
}: {
  state: ResultsCardState;
  onChange: (next: ResultsCardState) => void;
}) {
  const { t, locale } = useLocale();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [regionPicker, setRegionPicker] = useState(false);
  const { data: regions } = useQuery({
    queryKey: ['catalog', 'regions', locale],
    queryFn: () => fetchRegions(locale),
    staleTime: 10 * 60_000,
  });
  const regionName = (code: string) => regions?.find((r) => r.slug === code)?.name ?? code;

  const summaryParts: string[] = [];
  if (state.regions.length) summaryParts.push(state.regions.map(regionName).join(', '));
  if (state.minBudget !== undefined)
    summaryParts.push(t('results.refine.sumFrom', { amount: formatCount(state.minBudget) }));
  if (state.maxBudget !== undefined)
    summaryParts.push(t('results.refine.sumTo', { amount: formatCount(state.maxBudget) }));
  if (state.exclude.length)
    summaryParts.push(t('results.refine.excludeCount', { count: state.exclude.length }));
  if (state.kinds.length)
    summaryParts.push(state.kinds.map((k) => t(`results.kind.${k}`)).join(', '));
  if (state.customer) summaryParts.push(`${t('results.refine.customer')} ${state.customer}`);

  return (
    <div className="mt-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-9 w-full items-center gap-1.5 py-1 text-left text-[13.5px] font-bold text-brand"
      >
        <SlidersIcon className="h-[15px] w-[15px] shrink-0" />
        <span>{t('results.refine')}</span>
        <svg
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
          className={`h-2.5 w-2.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M3 4.5l3 3 3-3" />
        </svg>
        {!open && (
          <span
            className={`ml-1.5 min-w-0 truncate text-[12.5px] font-medium ${
              summaryParts.length ? 'text-ink-soft' : 'text-ink-muted'
            }`}
          >
            {summaryParts.length ? summaryParts.join(' · ') : t('results.refine.empty')}
          </span>
        )}
      </button>

      {open && (
        <div
          id={panelId}
          className="mt-1.5 grid grid-cols-1 gap-3.5 rounded-md border border-edge-light bg-bone p-3.5 md:grid-cols-2 md:gap-x-5 md:gap-y-4"
        >
          <div className="min-w-0">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
              {t('results.refine.region')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {state.regions.map((code) => (
                <span
                  key={code}
                  className="inline-flex h-7 max-w-full items-center gap-1 overflow-hidden rounded-pill border border-edge bg-bone-surface pl-[11px] pr-1 text-[12.5px] font-semibold text-ink-soft"
                >
                  <span className="truncate">{regionName(code)}</span>
                  <RemoveButton
                    label={regionName(code)}
                    onClick={() =>
                      onChange({ ...state, regions: state.regions.filter((r) => r !== code) })
                    }
                  />
                </span>
              ))}
              {state.regions.length < RESULTS_MAX_CHIPS && (
                <button
                  type="button"
                  aria-expanded={regionPicker}
                  onClick={() => setRegionPicker((o) => !o)}
                  className="inline-flex h-7 items-center rounded-pill border border-dashed border-[#c9c7bf] px-[11px] text-[12.5px] font-semibold text-brand"
                >
                  {t('results.refine.addRegion')}
                </button>
              )}
            </div>
            {regionPicker && (
              <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-edge bg-bone-surface p-1">
                {(regions ?? [])
                  .filter((r) => !state.regions.includes(r.slug))
                  .map((r) => (
                    <button
                      key={r.slug}
                      type="button"
                      onClick={() => {
                        onChange({ ...state, regions: [...state.regions, r.slug] });
                        setRegionPicker(false);
                      }}
                      className="block w-full rounded px-2.5 py-2 text-left text-[13.5px] text-ink hover:bg-bone"
                    >
                      {r.name}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <BudgetFields state={state} onChange={onChange} />

          <div className="min-w-0 md:col-span-2">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
              {t('results.refine.exclude')}
            </p>
            <ExcludeField state={state} onChange={onChange} />
          </div>

          {/* Every filter is visible as soon as «Уточнить» is open — no second
              «Ещё:» level to click through (owner, 2026-10-10: «жмёшь и жмёшь
              зачем-то — бесконечное раскрывание»). */}
          {countryConfig.features.priceIntel && (
            <div className="min-w-0">
              <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
                {t('results.refine.kind')}
              </p>
              <div
                className="flex flex-wrap gap-1.5"
                role="group"
                aria-label={t('results.refine.kind')}
              >
                {KINDS.map((k) => {
                  const on = state.kinds.includes(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        onChange({
                          ...state,
                          kinds: on ? state.kinds.filter((x) => x !== k) : [...state.kinds, k],
                        })
                      }
                      className={`inline-flex h-7 items-center rounded-pill border px-[11px] text-[12.5px] font-semibold ${
                        on
                          ? 'border-brand bg-brand-light text-brand'
                          : 'border-edge bg-bone-surface text-ink-soft'
                      }`}
                    >
                      {t(`results.kind.${k}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="min-w-0">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
              {t('results.refine.customer')}
            </p>
            <CustomerField state={state} onChange={onChange} />
          </div>
        </div>
      )}
    </div>
  );
}

function digitsOnly(v: string): string {
  return v.replace(/\D/g, '').slice(0, 15);
}

function BudgetFields({
  state,
  onChange,
}: {
  state: ResultsCardState;
  onChange: (next: ResultsCardState) => void;
}) {
  const { t } = useLocale();
  const [min, setMin] = useState(state.minBudget !== undefined ? String(state.minBudget) : '');
  const [max, setMax] = useState(state.maxBudget !== undefined ? String(state.maxBudget) : '');
  useEffect(
    () => setMin(state.minBudget !== undefined ? String(state.minBudget) : ''),
    [state.minBudget],
  );
  useEffect(
    () => setMax(state.maxBudget !== undefined ? String(state.maxBudget) : ''),
    [state.maxBudget],
  );

  function apply() {
    const lo = min ? Number(min) : undefined;
    const hi = max ? Number(max) : undefined;
    if (lo === state.minBudget && hi === state.maxBudget) return;
    const next = { ...state };
    delete next.minBudget;
    delete next.maxBudget;
    onChange({
      ...next,
      ...(lo !== undefined ? { minBudget: lo } : {}),
      ...(hi !== undefined ? { maxBudget: hi } : {}),
    });
  }
  const field = (value: string, set: (v: string) => void, label: string, placeholder: string) => (
    <label className="flex h-10 min-w-0 flex-1 items-center gap-1.5 rounded-md border border-edge bg-bone-surface px-2.5 text-[12.5px] text-ink-muted">
      {label}
      <input
        inputMode="numeric"
        value={value ? formatCount(Number(value)) : ''}
        onChange={(e) => set(digitsOnly(e.target.value))}
        onBlur={apply}
        onKeyDown={(e) => e.key === 'Enter' && apply()}
        placeholder={placeholder}
        className="w-full min-w-0 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-[13px] placeholder:text-[#a3a0b3] md:text-[14px]"
      />
    </label>
  );
  return (
    <div className="min-w-0">
      <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
        {t('results.refine.sum')}
      </p>
      <div className="flex items-center gap-2">
        {field(min, setMin, t('results.refine.from'), '0')}
        <span className="text-ink-muted">—</span>
        {field(max, setMax, t('results.refine.to'), t('results.refine.any'))}
      </div>
    </div>
  );
}

function ExcludeField({
  state,
  onChange,
}: {
  state: ResultsCardState;
  onChange: (next: ResultsCardState) => void;
}) {
  const { t } = useLocale();
  const [draft, setDraft] = useState('');
  function commit() {
    const w = normalizeWord(draft);
    setDraft('');
    if (!w || state.exclude.includes(w) || state.exclude.length >= RESULTS_MAX_CHIPS) return;
    onChange({ ...state, exclude: [...state.exclude, w] });
  }
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-edge bg-bone-surface px-2 py-[5px]">
      {state.exclude.map((w) => (
        <span
          key={w}
          className="inline-flex h-[26px] max-w-full items-center gap-1 overflow-hidden rounded-pill border border-orange-ink/25 bg-[#fff5f0] pl-[11px] pr-1 text-[12.5px] font-semibold text-orange-ink"
        >
          <span className="truncate line-through decoration-1">{w}</span>
          <RemoveButton
            label={w}
            onClick={() => onChange({ ...state, exclude: state.exclude.filter((x) => x !== w) })}
          />
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, 64))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && draft === '' && state.exclude.length) {
            onChange({ ...state, exclude: state.exclude.slice(0, -1) });
          }
        }}
        onBlur={commit}
        aria-label={t('results.refine.exclude')}
        placeholder={t('results.refine.excludePlaceholder')}
        className="min-w-[6rem] flex-1 bg-transparent text-base text-ink outline-none placeholder:text-[13px] placeholder:text-ink-muted md:text-[13px]"
      />
    </div>
  );
}

function CustomerField({
  state,
  onChange,
}: {
  state: ResultsCardState;
  onChange: (next: ResultsCardState) => void;
}) {
  const { t } = useLocale();
  const [value, setValue] = useState(state.customer ?? '');
  useEffect(() => setValue(state.customer ?? ''), [state.customer]);
  function apply() {
    const tin = /^\d{9,14}$/.test(value) ? value : undefined;
    if (tin === state.customer) return;
    const next = { ...state };
    delete next.customer;
    onChange(tin ? { ...next, customer: tin } : next);
  }
  return (
    <label className="flex h-10 min-w-0 items-center gap-1.5 rounded-md border border-edge bg-bone-surface px-2.5 text-[12.5px] text-ink-muted md:w-64">
      {t('results.refine.customerLabel')}
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => setValue(digitsOnly(e.target.value).slice(0, 14))}
        onBlur={apply}
        onKeyDown={(e) => e.key === 'Enter' && apply()}
        className="w-full min-w-0 flex-1 bg-transparent text-base tabular-nums text-ink outline-none md:text-[14px]"
      />
    </label>
  );
}

/** The «Изменено: …» / «Обновлено: …» item list. */
function changeList(
  c: ResultsChange,
  t: (k: string, p?: Record<string, string | number>) => string,
  label: (kind: 'word' | 'category' | 'ikpu', value: string) => string,
): string {
  const parts = [
    ...c.added.map((x) => `+ «${label(x.kind, x.value)}»`),
    ...c.removed.map((x) => `− «${label(x.kind, x.value)}»`),
    ...c.axes.map((a) => t(`results.change.${a}`)),
  ];
  return parts.join(', ');
}

/** Count line, the wall line, and «Сохранить / Обновить поиск». */
function CardFooter({
  state,
  summary,
  showWall,
  countTrailing,
  categoryName,
  ikpuNames,
}: {
  state: ResultsCardState;
  summary: { month: number; week: number; today: number } | undefined;
  showWall: boolean;
  countTrailing?: ReactNode;
  categoryName: (slug: string) => string;
  ikpuNames: Record<string, string>;
}) {
  const { t, locale } = useLocale();
  const { status: authStatus } = useAuth();
  const authed = authStatus === 'authed';
  const navigate = useLocaleNavigate();
  const qc = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  const { data: saved } = useQuery({
    queryKey: ['results-search'],
    queryFn: getResultsSearch,
    enabled: authed,
  });
  const savedState = saved ? fromResultsCriteria(saved.criteria as ResultsCriteria) : null;
  const change = savedState ? diffResultsState(savedState, state) : null;
  const unchanged = change !== null && isNoChange(change);
  const empty = !hasResultsState(state);

  const label = (kind: 'word' | 'category' | 'ikpu', value: string) =>
    kind === 'category'
      ? categoryName(value)
      : kind === 'ikpu'
        ? (ikpuNames[value] ?? value)
        : value;

  // A new card state clears the last «Обновлено» line — it described the old one.
  const stateKey = JSON.stringify(toResultsCriteria(state));
  useEffect(() => setMessage(null), [stateKey]);

  const mutation = useMutation({
    mutationFn: () => {
      const criteria = toResultsCriteria(state);
      if (!criteria) throw new Error('empty');
      // The name is fixed («Результаты» / «Natijalar»), chosen by the API.
      return saveResultsSearch({ criteria, lang: locale });
    },
    onSuccess: async (r) => {
      qc.setQueryData(['results-search'], r.data);
      await qc.invalidateQueries({ queryKey: ['results-search'] });
      setMessage(
        r.created
          ? t('results.created')
          : r.changed.length
            ? t('results.updated', {
                list: r.changed.map((a) => t(`results.change.${a}`)).join(', '),
              })
            : null,
      );
      // The Telegram connect banner re-surfaces on a saved search (#22) —
      // alerts are configured there, not on this card.
      window.dispatchEvent(new CustomEvent('tenders:saved-search'));
    },
    onError: () => setMessage(t('results.save.error')),
  });

  function onSave() {
    if (empty) return;
    if (!authed) {
      const criteria = toResultsCriteria(state);
      if (!criteria) return;
      // Same hand-off as the catalogue's «Сохранить поиск»: stash, register,
      // replayed by PendingSavedSearchSync once signed in.
      stashPendingSavedSearch({ results: { criteria } });
      navigate('/register');
      return;
    }
    mutation.mutate();
  }

  const isSavedView = authed && saved != null && unchanged;
  const buttonLabel = isSavedView
    ? t('results.saved')
    : authed && saved != null
      ? t('results.update')
      : t('results.save');

  const note = empty
    ? t('results.save.empty')
    : !authed
      ? t('results.save.guest')
      : saved != null && change && !unchanged
        ? t('results.changed', { list: changeList(change, t, label) })
        : null;

  return (
    <div className="mt-4 border-t border-edge-light pt-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[13px] text-ink-soft" data-testid="results-count" aria-live="polite">
              {summary ? (
                <>
                  <b className="font-bold text-ink">
                    {t('results.count.found', {
                      count: formatCount(summary.month),
                      word: resultsWord(summary.month, locale),
                    })}
                  </b>{' '}
                  {t('results.count.period')} ·{' '}
                  <span className="font-semibold text-status-active-text">
                    {t('results.count.week', { count: formatCount(summary.week) })}
                  </span>{' '}
                  · {t('results.count.today', { count: formatCount(summary.today) })}
                </>
              ) : (
                <span className="text-ink-muted">{t('results.count.loading')}</span>
              )}
            </p>
            {countTrailing}
          </div>
          {showWall && (
            <p
              className="mt-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5"
              data-testid="results-wall"
            >
              <LockedChip>{t('results.wall.line')}</LockedChip>
              <LocaleLink
                to="/plans?src=paywall"
                onClick={() => track('upgrade_cta_clicked', { source: 'resultsPage' })}
                className="text-[12.5px] font-semibold text-ink-soft underline decoration-[#c9c7bf] underline-offset-[3px] hover:text-brand"
              >
                {t('results.wall.cta')}
              </LocaleLink>
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-stretch md:items-end">
          <button
            type="button"
            onClick={onSave}
            disabled={empty || isSavedView || mutation.isPending}
            aria-disabled={empty || isSavedView}
            className={`inline-flex h-[46px] items-center justify-center gap-2 rounded-md px-[18px] text-[15px] font-bold transition-colors md:h-10 md:text-[14px] ${
              isSavedView
                ? 'border border-status-active-text/30 bg-status-active-bg text-status-active-text'
                : 'bg-brand text-white hover:bg-brand-mid disabled:cursor-not-allowed disabled:opacity-50'
            }`}
          >
            {isSavedView && <CheckIcon className="h-4 w-4" />}
            {buttonLabel}
          </button>
        </div>
      </div>
      {(message || note) && (
        <p
          role="status"
          className={`mt-2.5 text-center text-[12.5px] md:text-right ${
            message || (note && saved != null && !unchanged && !empty)
              ? 'text-brand'
              : 'text-ink-muted'
          }`}
        >
          {message ?? note}
        </p>
      )}
    </div>
  );
}
